/* eslint-disable import/no-unused-modules */
import React from 'react'

import { COLORS, styled } from '../../../theme'
import { Alert } from '../../Alert'
import { Box } from '../../Box'
import { DataGrid } from '../../DataGrid'
import { Stack } from '../../Stack'
import { DeleteRowModal, DrawerDataGrid, EditRowDataModal, EditToolbar } from './components'
import { ListFieldData, OnChangeCustomEvent } from './definitions'
import { useListField } from './hooks/useListField'

export interface ListFieldProps {
  /**
   * The `apiData?: ListFieldData` in the `ListFieldProps` interface is defining a prop called
   * `apiData` for the `ListField` component. The `?` indicates that the prop is optional, meaning that
   * it doesn't have to be provided when using the `ListField` component. The type `ListFieldData`
   * specifies the expected data structure that can be passed as the `apiData` prop. This allows the
   * component to receive external data through the `apiData` prop if needed.
   */
  apiData?: ListFieldData
  /**
   * The `onChange?: (evt: OnChangeCustomEvent) => void` in the `ListFieldProps` interface is defining
   * a prop called `onChange` for the `ListField` component. The `?` indicates that the prop is
   * optional, meaning that it doesn't have to be provided when using the `ListField` component.
   */
  onChange?: (evt: OnChangeCustomEvent) => void
  /**
   * The `customEditCallback?: () => void` in the `ListFieldProps` interface is defining a prop called
   * `customEditCallback` for the `ListField` component. The `?` indicates that this prop is optional,
   * meaning that it doesn't have to be provided when using the `ListField` component.
   */
  customEditCallback?: () => void
  /**
   * The `customDeleteCallback?: () => void` in the `ListFieldProps` interface is defining a prop
   * called `customDeleteCallback` for the `ListField` component. The `?` indicates that this prop is
   * optional, meaning that it doesn't have to be provided when using the `ListField` component. This
   * prop is a function that takes no arguments and returns void, indicating that it is a callback
   * function that can be passed to the `ListField` component to handle custom delete actions or
   * functionality.
   */
  customDeleteCallback?: () => void
}

export const ListField = ({ apiData, onChange, customEditCallback, customDeleteCallback }: ListFieldProps) => {
  const gridHook = useListField(apiData, onChange, customEditCallback, customDeleteCallback)

  const { title, rows, columns, alertState, openHistoryDrawer, closeAlert, handleOpenEditElementModal } = gridHook

  const StyledStack = styled(Stack)`
    height: 100%;
    align-items: center;
    justify-content: center;
  `

  const StyledBox = styled(Box)`
    height: 500px;
    width: 100%;
    & .actions {
      color: ${COLORS.text.secondary};
    }
    & .textPrimary {
      color: ${COLORS.text.primary};
    }
  `
  const StyledDataGrid = styled(DataGrid)`
    & .MuiDataGrid-main {
      & div div div div .MuiDataGrid-cell {
        border-bottom: none;
      }
      & div div div .MuiDataGrid-row {
        padding-left: 20px;
        /* padding-right: 20px; */
      }
    }
  `

  const eachRowHasId = rows && rows.every((row) => row.id !== undefined)
  return (
    <StyledBox>
      {columns.length > 0 && eachRowHasId ? (
        <StyledDataGrid
          data-test-id={`list_field-root-${title}`}
          rows={rows}
          columns={columns}
          slots={{
            toolbar: () => (
              <EditToolbar
                title={title}
                addItemCallback={() => handleOpenEditElementModal()}
                viewHistoryCallback={() => openHistoryDrawer()}
              ></EditToolbar>
            ),
            noRowsOverlay: () => <StyledStack>This space is empty for now</StyledStack>,
          }}
          hideFooter
          rowSpacingType="margin"
          columnHeaderHeight={0}
        />
      ) : (
        <StyledStack>
          There was an error while retrieving the data or the columns configuration provided is not compatible with the
          component or the data
        </StyledStack>
      )}
      <Alert alertMessage={alertState.message} onClose={() => closeAlert()} isShowing={alertState.visible} />
      <DrawerDataGrid
        rows={gridHook.rows}
        columns={gridHook.columns}
        drawerOpen={gridHook.drawerOpen}
        handleCloseDrawer={() => gridHook.handleCloseDrawer()}
      />
      <EditRowDataModal {...gridHook} />
      <DeleteRowModal {...gridHook} />
    </StyledBox>
  )
}
